import { ColumnDef } from '@tanstack/react-table';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { Dropdown } from 'react-bootstrap';
import { JobClient } from 'data/users';
import { Link, useNavigate } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { DELETE_CUSTOMER } from 'services/api/customer';
import { SITE_ENDPOINT } from 'Constant';
import { toast } from 'react-hot-toast';
import { useQueryClient } from 'react-query';

const handleDelete = async (customerId: string) => {
  try {
    await DELETE_CUSTOMER(customerId);
    toast.success('Customer deleted successfully');
  } catch (error: any) {
    toast.error('Error deleting customer:', error);
  }
};

export const customerTableColumns = (
  canEdit = false,
  canDelete = false
): ColumnDef<JobClient>[] => {
  return [
    {
      accessorKey: 'organizationName',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-warning-100 rounded me-2">
              <FeatherIcon
                icon="briefcase"
                size={16}
                className="text-warning-600 dark__text-warning-300"
              />
            </div>
            <span>Organization name</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const organizationName = original.organizationName;
        return organizationName;
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 border-end fw-semi-bold text-1000'
        }
      }
    },
    {
      accessorKey: 'phoneNumber',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-primary-100 rounded me-2">
              <FeatherIcon
                icon="phone"
                size={16}
                className="text-primary-600 dark__text-primary-300"
              />
            </div>
            <span>Phone</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { phoneNumber } = original;
        return (
          <Link className="text-1000 text-nowrap" to={`tel:${phoneNumber}`}>
            {phoneNumber}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%', minWidth: '180px' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'address',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-success-100 rounded me-2">
              <FeatherIcon
                icon="map-pin"
                size={16}
                className="text-success-600 dark__text-success-300"
              />
            </div>
            <span>Address</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { address } = original;
        return (
          <Link className="text-1000 text-nowrap" to={`mailto:${address}`}>
            {address}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'country',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-info-100 rounded me-2">
              <FeatherIcon
                icon="globe"
                size={16}
                className="text-info-600 dark__text-info-300"
              />
            </div>
            <span>Country</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => original.country,
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 border-end fw-semi-bold text-1000'
        }
      }
    },
    {
      accessorKey: 'customerRepresentative.firstName',
      enableSorting: true,
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-danger-100 rounded me-2">
              <FeatherIcon
                icon="user"
                size={16}
                className="text-danger-600 dark__text-danger-300"
              />
            </div>
            <span>Customer Representative</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const firstName = original?.customerRepresentative?.firstName;
        const lastName = original?.customerRepresentative?.lastName;
        return (
          <div className="d-flex align-items-center">
            <div>
              {firstName ? (
                <Link to={``} className="fs-8 fw-bold">
                  {firstName} {lastName}
                </Link>
              ) : (
                <Link
                  to={`/home/apps/representative/create-rep`}
                  className="fs-8 fw-bold"
                >
                  {'No Representative Create One'}
                </Link>
              )}
            </div>
          </div>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 border-end fw-semi-bold text-1000'
        }
      }
    },
    {
      id: 'leadDropdown',
      enableSorting: false,
      header: () => '',
      cell: ({ row: { original } }) => {
        const customerId = (original as any).id || original._id;
        const navigate = useNavigate();
        const queries = useQueryClient();

        if (!canEdit && !canDelete) {
          return null;
        }

        const handleDeleteClick = async (customerId: string) => {
          try {
            await handleDelete(customerId);
            queries.invalidateQueries('getAllCustomers');
            queries.invalidateQueries('get_customer');
            queries.invalidateQueries('getCustomers');
            navigate(`/home/apps/${SITE_ENDPOINT.COMPANY.INDEX}`);
          } catch (error: any) {
            toast.error('Error deleting customer:', error);
          }
        };

        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <>
                {canEdit && (
                  <Dropdown.Item
                    eventKey="1"
                    onClick={() =>
                      navigate(
                        `/home/apps/${SITE_ENDPOINT.COMPANY.VIEW_COMPANY}/${customerId}`
                      )
                    }
                  >
                    View
                  </Dropdown.Item>
                )}
                {canEdit && (
                  <Dropdown.Item
                    eventKey="2"
                    onClick={() =>
                      navigate(
                        `/home/apps/${SITE_ENDPOINT.COMPANY.EDIT_COMPANY}/${customerId}`
                      )
                    }
                  >
                    Edit
                  </Dropdown.Item>
                )}
                {canDelete && <Dropdown.Divider />}
                {canDelete && (
                  <Dropdown.Item
                    eventKey="4"
                    className="text-danger"
                    onClick={() => handleDeleteClick(customerId)}
                  >
                    Delete
                  </Dropdown.Item>
                )}
              </>
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        headerProps: {
          style: { width: '5%' },
          className: 'ps-4 pe-5'
        },
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];
};
